'use client';

import { clearRegistrationAuthComplete, hasRegistrationAuthSessionFlag } from '@shared/auth';
import { cn } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { usePostSeonSession } from '@shared/hooks';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useLayoutEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { MultistepForm } from '../../form-controls/MultistepForm/MultistepForm';
import { OfferCompact } from './components/OfferCompact/OfferCompact';
import { OfferPrimary } from './components/OfferPrimary/OfferPrimary';
import { OfferPrimaryLoading } from './components/OfferPrimary/OfferPrimary.loading';
import { discardOrphanRegistrationAuthReturn, hasRegistrationAuthReturnParam } from './hooks/registrationAuthResume';
import { useStepsConfig } from './stepsConfig';

const debug = createDebug({ isEnabled: false });

function RegistrationForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const hasAuthReturnParam = hasRegistrationAuthReturnParam(searchParams);
  const [hasDiscardedStaleAuthReturn, setHasDiscardedStaleAuthReturn] = useState(false);
  const isResumingAfterAuth = hasAuthReturnParam && !hasDiscardedStaleAuthReturn;
  const [isFirstStep, setIsFirstStep] = useState(!hasAuthReturnParam);

  // Post the SEON session to the API (registration only)
  usePostSeonSession();

  // Define all steps with their components, titles, and schemas
  const { steps, form } = useStepsConfig();

  useLayoutEffect(() => {
    const hasSessionFlag = hasRegistrationAuthSessionFlag();

    if (!hasAuthReturnParam && hasSessionFlag) {
      clearRegistrationAuthComplete();
      return;
    }

    if (!hasAuthReturnParam || hasSessionFlag) {
      return;
    }

    discardOrphanRegistrationAuthReturn(href => router.replace(href), searchParams);
    setHasDiscardedStaleAuthReturn(true);
    setIsFirstStep(true);
  }, [hasAuthReturnParam, router, searchParams]);

  // Handle form submission (prevent default, allow password managers to work)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission is handled by individual step logic
    // This handler just prevents page reload and allows password managers to detect the submit event
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row min-h-dvh" autoComplete="off" data-testid="registration-form">
        {/* Offer Panel - 50% width on desktop, full width on mobile, visible on first step on mobile */}
        <aside
          className={cn('md:w-1/2 flex flex-col justify-center bg-background-inverse dark:bg-backround text-foreground-inverse', !isFirstStep && 'hidden md:flex')}
          data-track-container="Value Proposition"
          data-testid="offer-panel"
        >
          <Suspense fallback={<OfferPrimaryLoading />}>
            <OfferPrimary />
          </Suspense>
        </aside>

        {/* Content Panel - 50% width on desktop, full width on mobile */}
        <div
          className={cn('md:w-1/2 flex flex-col flex-1 bg-background relative', isFirstStep && '-mt-2 rounded-s-lg rounded-e-lg md:mt-0 md:rounded-none')}
          data-track-feature="Registration"
          data-testid="content-panel"
        >
          <MultistepForm
            steps={steps}
            offerComponent={<OfferCompact />}
            className="flex flex-col flex-1 md:place-items-center"
            initialStatus={isResumingAfterAuth ? 'success' : undefined}
            initialStepId={isResumingAfterAuth ? 'processing' : undefined}
            onStepChange={setIsFirstStep}
            onComplete={async formData => {
              debug.log('Registration form completed', formData);
            }}
          />
        </div>
      </form>
    </FormProvider>
  );
}

export { RegistrationForm };
